import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';
import { UploadForm } from '../common/UploadForm';
import * as ActionTypes from '../../../../constants/ActionTypes';
import { saveConfigurationFile as saveConfigurationFileAction } from '../../../../actions/configuration';
import { OnLeaveSaveFileDialog } from '../../../modals/editFile/OnLeaveSaveFileDialog';
import GenerateSchema from 'generate-schema';
import { configurationSystemDefault } from '../../../../constants/SystemDefaults';
import { RESUME_BOOKING_BOTTOM_DISTANCE } from '../../../../constants/RoomsConfiguration';
import { getUpgradedJsonSchema } from '../../manage/utils';
import SaveAndCancelBottomButtons from '../common/saveAndCancelBottomButtons/saveAndCancelBottomButtons';
import { MetaData } from 'components/pages/manage/common/MetaData';
import JsonCodeEditorPanel from 'components/widgets/CodeEditor/JsonCodeEditorPanel';
import { SchemaValidationErrorMessage } from 'components/pages/manage/common/SchemaValidationErrorMessage';
import { sanitizeConfig } from '../../../../utils/configuration-utils';

class EditConfig extends Component {
    state = {
        initialConfig: this.props.currentConfig,
        isFileValid: true
    };

    componentDidMount() {
        if (this.state.initialConfig) {
            this.props.onFileChange(this.state.initialConfig, this.state.initialConfig, false);
        }

        // generate config schema on the fly
        const configSchemaFromLibrary = GenerateSchema.json(configurationSystemDefault);
        this.configSchema = getUpgradedJsonSchema(configSchemaFromLibrary, 'configSchema', configurationSystemDefault);

        // enforce positive numeric constraints for bottom distance fields
        if (this.configSchema.properties.resumeBookingBottomDistance) {
            this.configSchema.properties.resumeBookingBottomDistance.minimum = RESUME_BOOKING_BOTTOM_DISTANCE.MIN;
            this.configSchema.properties.resumeBookingBottomDistance.maximum = RESUME_BOOKING_BOTTOM_DISTANCE.MAX;
        }
        if (this.configSchema.properties.resumeBookingMobileBottomDistance) {
            this.configSchema.properties.resumeBookingMobileBottomDistance.minimum = RESUME_BOOKING_BOTTOM_DISTANCE.MIN;
            this.configSchema.properties.resumeBookingMobileBottomDistance.maximum = RESUME_BOOKING_BOTTOM_DISTANCE.MAX;
        }
    }

    onChange = data => {
        this.props.onFileChange(this.state.initialConfig, data.value, data.valid);
        this.setState({ isFileValid: data.valid });
    };

    handleSetFileData = (selected, newParams, configCode, configToSave) => {
        const configData = sanitizeConfig(configToSave, _.get(this.props.selected, 'retailProductCategories'));
        return this.props.setConfigFileData(selected, newParams, configCode, configData);
    };

    render() {
        const selectedConfig = (this.props.configList || []).find(
            config => config.ContentCode === this.props.configCode
        );
        const { isFileValid } = this.state;
        const configCode = this.props.configCode;
        const currentValue = this.props.configToSave;
        const metadata = _.get(selectedConfig, 'MetaData');
        const fileChanged = this.props.configCode
            ? !_.isEqual(this.state.initialConfig, this.props.configToSave)
            : !!this.props.configToSave;
        const shouldLeavePageOnCancel = this.props.configCode ? true : !this.props.configToSave;

        return (
            <div>
                {configCode || currentValue || currentValue === '' ? (
                    <div>
                        <div className="spark-panel spark-margin-top edit-JSON-form">
                            <div className="spark-panel__content">
                                <div className="title spark-mar-t-1">
                                    {configCode ? (
                                        <h1>
                                            Edit - Configuration: <span className="Strong">{configCode}</span>
                                        </h1>
                                    ) : (
                                        <h1>Create - Configuration</h1>
                                    )}
                                </div>
                                {metadata && <MetaData metadata={metadata} />}
                                <div className="json-code-editor-panel__wrapper">
                                    <div className="json-code-editor-panel">
                                        <div className="json-code-editor-panel__editor-wrap">
                                            {!isFileValid && <SchemaValidationErrorMessage />}
                                            {(currentValue || currentValue === '') && (
                                                <JsonCodeEditorPanel
                                                    onChange={this.onChange}
                                                    currentValue={currentValue}
                                                    schema={this.configSchema}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <SaveAndCancelBottomButtons
                            level={this.props.level}
                            chainId={this.props.chainId}
                            hotelId={this.props.hotelId}
                            isSaveBtnDisabled={!this.props.configFileChanged}
                            setFileData={this.handleSetFileData}
                            selected={this.props.selected}
                            code={this.props.configCode}
                            list={this.props.configList}
                            fileToSave={this.props.configToSave}
                            titleName="Configuration"
                            onAfterSave={() => {
                                this.props.router.push('/manage/manage-configuration');
                            }}
                            onCancelClick={() => {
                                this.props.router.push('/manage/manage-configuration');
                            }}
                        />
                    </div>
                ) : (
                    <UploadForm
                        type="Configuration"
                        level={this.props.level}
                        dropZoneDescription="A valid JSON config file is expected. Download an existing config file to determine parameters expected."
                        onLoad={data => {
                            this.props.onFileChange(this.state.initialConfig, data, false);
                            this.setState({
                                initialConfig: data
                            });
                        }}
                    />
                )}
                <OnLeaveSaveFileDialog
                    router={this.props.router}
                    route={this.props.route}
                    level={this.props.level}
                    chainId={this.props.chainId}
                    hotelId={this.props.hotelId}
                    onPageLeave={this.props.onPageLeave}
                    fileChanged={fileChanged}
                    shouldLeavePageOnCancel={shouldLeavePageOnCancel}
                    titleName="Configuration"
                />
            </div>
        );
    }
}

EditConfig.propTypes = {
    level: PropTypes.string.isRequired,
    selected: PropTypes.object,
    configList: PropTypes.array,
    configCode: PropTypes.string,
    currentConfig: PropTypes.object,
    configToSave: PropTypes.object,
    configFileChanged: PropTypes.boolean
};

const mapStateToProps = state => {
    const {
        configCode,
        currentConfig,
        configFileToSave,
        configFileChanged
    } = state.app.data.configuration.configuration;

    const { configList, level, chain, selectedHotel } = state.app.selected;

    return {
        selected: state.app.selected,
        level,
        chainId: chain.Id,
        hotelId: selectedHotel.id,
        configCode,
        currentConfig: configCode ? currentConfig : null,
        configToSave: configFileToSave,
        configFileChanged,
        configList
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onCancelation: () => {
            dispatch({ type: ActionTypes.SET_CONFIG_FILE_READY_TO_SAVE, configFileChanged: false });
            dispatch({ type: ActionTypes.SET_CONFIG_FILE_DATA, configToSave: null });
        },
        onFileChange: (initialData, configToSave, isCurrentDataValid) => {
            let configFileChanged = false;
            if (isCurrentDataValid && !_.isEmpty(configToSave)) {
                configFileChanged = !_.isEqual(initialData, configToSave);
            }

            dispatch({ type: ActionTypes.SET_CONFIG_FILE_READY_TO_SAVE, configFileChanged });
            dispatch({ type: ActionTypes.SET_CONFIG_FILE_DATA, configToSave });
        },
        onPageLeave: () => {
            dispatch({ type: ActionTypes.REFRESH_SELECTED_CONFIG_FILE });
        },
        setConfigFileData: (selected, newParams, configCode, configToSave) => {
            return dispatch(saveConfigurationFileAction(selected, configCode, configToSave, newParams)).then(() => {
                return true;
            });
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EditConfig));



// WEBPACK FOOTER //
// ./src/components/pages/manage/editConfig/index.js